import { Button, useTheme } from '@mui/material';
import { Typography } from '../../../components/Typography/Typography';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/download-blue.svg';
import { useParams } from 'react-router';
import { useCompanyById } from '../../../queries/useCompanies';
import { useKpiMatrix } from '../../SIngleCompany/hooks/useKpiMatrix';
import { useCallback, useMemo } from 'react';
import { CORE_KPI_FORMAT, CoreKpiRequestFrequency, SURVEY_FREQUENCY, User } from '../../../types';
import { COMPANY_KPIS_EXCEL_HEADERS } from '../../../constants/defaultValues';
import { useCoreKpiCategories } from '../../../queries/useCoreKpiCategories';
import ExcelJS, { Alignment } from 'exceljs';
import FileSaver from 'file-saver';
import { useAtomValue } from 'jotai';
import { userState } from '../../../state/UIState';

export const DownloadExcelButton = () => {
  const { colors } = useTheme();
  const { companyId } = useParams();
  const { data: company } = useCompanyById(Number(companyId));
  const { getTableData } = useKpiMatrix();
  const { data: coreKpiCategories } = useCoreKpiCategories();
  const user = useAtomValue(userState) as User;

  const companyKpisDataFrequency = useMemo(() => {
    return (company?.kpisDataFrequency as CoreKpiRequestFrequency) || SURVEY_FREQUENCY.QUARTERLY;
  }, [company]);

  const periodCols = useMemo(() => {
    if (!company) return [];
    return company.kpisData[companyKpisDataFrequency]?.map((item) => item.period);
  }, [company, companyKpisDataFrequency]);

  const exportKpis = useCallback(async () => {
    if (!company || !coreKpiCategories) return;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = user?.fullName || '';
    workbook.created = new Date();
    workbook.properties.date1904 = true;

    const worksheet = workbook.addWorksheet(`${company?.name} kpis`);

    const periodColsHeaders = periodCols?.map((periodCol) => ({
      header: periodCol,
      key: periodCol,
    }));

    const columns = [...COMPANY_KPIS_EXCEL_HEADERS, ...periodColsHeaders]?.map((col) => {
      let width = 15;

      if (col.key === 'name') {
        width = 35;
      }

      if (col.key === 'metric') {
        width = 20;
      }

      const isInPeriodCol = periodCols?.includes(col.key);

      return {
        ...col,
        width,
        style: {
          alignment: {
            wrapText: true,
            vertical: 'top',
            horizontal: isInPeriodCol ? 'right' : 'left',
          } as Alignment,
        },
      };
    });

    worksheet.columns = columns;

    const baseKeys = COMPANY_KPIS_EXCEL_HEADERS.map((header) => header.key);
    const keys = [...baseKeys, ...periodCols];

    const kpisTabledata = getTableData(
      companyKpisDataFrequency,
      company.kpisData,
      coreKpiCategories
    );

    const worksheetRows = kpisTabledata.map((item) => {
      const row = keys.map((key) => {
        if (typeof item[key] === 'object') {
          return item[key].value || item[key].value === 0 ? item[key].value : '';
        }
        return item[key] || item[key] === 0 ? item[key] : '';
      });
      return row;
    });

    worksheetRows.forEach((worksheetRow) => {
      worksheet.addRow(worksheetRow);
    });

    worksheet.eachRow((row) => {
      const formatCell = row.getCell('format');

      if (formatCell.value === CORE_KPI_FORMAT.TEXT) {
        periodCols.forEach((period) => {
          const periodCell = row.getCell(period);

          periodCell.alignment = {
            wrapText: true,
            vertical: 'top',
            horizontal: 'left',
          };
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `${company?.name}_kpis.xlsx`);
  }, [
    company,
    companyKpisDataFrequency,
    coreKpiCategories,
    getTableData,
    periodCols,
    user?.fullName,
  ]);

  return (
    <Button
      onClick={exportKpis}
      variant='text'
      startIcon={<DownloadIcon />}
      style={{ minWidth: 'auto' }}
    >
      <Typography variant='subtitle2' color={colors.accent[50]}>
        Download Actual Data in Excel
      </Typography>
    </Button>
  );
};
